import {
  FluidLendingResolverAbi,
  FluidLiquidityResolverAbi,
  FluidVaultResolverAbi,
} from '@1delta/abis'
import { zeroAddress } from 'viem'
import {
  buildFluidCall,
  getFluidPublicDataConverter,
} from '../../lending/public-data/fluid'
import { MulticallRetryFunction, GenericTokenList } from '../../types'
import { parseRawAmount } from '../../utils/parsing'
import { VaultMarketExposure } from '../exposure'
import { fetchAssetDecimals } from '../assetDecimals'
import { buildFluidFTokensCall } from './publicCallBuild'
import { getFluidFTokensConverter } from './publicCallParse'
import { FluidFTokens } from './types'

/**
 * One-shot fetcher for all Fluid fTokens on a chain.
 *
 * Wraps build + multicall + parse so callers that just want "the fTokens
 * snapshot" don't need to hand-wire the pipeline.
 *
 * @param chainId       target chain
 * @param multicallRetry  provider-level multicall executor (same one used
 *                        by `getLenderPublicData`)
 * @param prices         optional price map keyed by oracle key / underlying
 * @param tokenList      optional token list for hydrating `asset` metadata
 *
 * @returns `FluidFTokens` — map keyed by lowercased underlying address, or
 *          an empty object if the chain has no resolver configured.
 */
export const fetchFluidFTokens = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<FluidFTokens> => {
  const calls = buildFluidFTokensCall(chainId)
  if (calls.length === 0) return {}

  const raw = await multicallRetry({
    chain: chainId,
    calls: calls as any,
    abi: calls.map(() => FluidLendingResolverAbi) as any,
  })

  const [converter] = getFluidFTokensConverter(chainId, prices, tokenList)
  const fTokens = converter(raw) ?? {}

  // Harden against an fToken whose share decimals diverge from its asset.
  // Today Fluid fTokens keep them aligned, so the parser used the share
  // `decimals()` for the (underlying-denominated) `totalAssets`. Read each
  // underlying's own decimals and reformat the asset-denominated fields with
  // it, so a future Fluid decimals offset can't silently 10^n the values.
  try {
    const underlyings = Object.values(fTokens)
      .map((f) => f.underlying)
      .filter((a): a is string => !!a && a !== zeroAddress)
    const assetDec = await fetchAssetDecimals(
      chainId,
      [...new Set(underlyings)],
      multicallRetry,
    )
    for (const f of Object.values(fTokens)) {
      const dec = assetDec.get(f.underlying)
      if (dec == null || dec === f.decimals) continue
      const price = f.priceUsd ?? 0
      f.decimals = dec
      f.totalAssetsFormatted = Number(parseRawAmount(f.totalAssets, dec))
      f.totalAssetsUsd = f.totalAssetsFormatted * price
      f.liquidityFormatted = Number(parseRawAmount(f.liquidity, dec))
      f.liquidityUsd = f.liquidityFormatted * price
    }
  } catch {
    // leave the share-decimal formatting in place on a read failure
  }

  // Attach borrowed-against-collateral exposures from the borrow vaults.
  // Best-effort: a failure here must not drop the fToken snapshot.
  try {
    const exposuresByUnderlying = await fetchFluidExposures(
      chainId,
      multicallRetry,
      prices,
      tokenList,
    )
    for (const underlying of Object.keys(fTokens)) {
      const exposures = exposuresByUnderlying.get(underlying)
      if (exposures?.length) fTokens[underlying].exposures = exposures
    }
  } catch {
    // leave exposures undefined
  }

  return fTokens
}

/**
 * Fetch Fluid borrow vaults and group them by the asset they borrow, so each
 * fToken can show what its supplied liquidity is being lent against.
 *
 * Returns a map: lowercased loan-asset address → exposures, where each
 * exposure is a borrow vault. `assets`/`assetsUsd` are the amount borrowed in
 * that asset, `weightPct` is the vault's share of the asset's total borrows,
 * `supplyApr` carries the vault's borrow rate (the rate that slice earns),
 * and `collateral` is the vault's collateral token.
 */
async function fetchFluidExposures(
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): Promise<Map<string, VaultMarketExposure[]>> {
  const out = new Map<string, VaultMarketExposure[]>()
  const vaultCalls = buildFluidCall(chainId)
  if (vaultCalls.length === 0) return out

  const rawVaults = await multicallRetry({
    chain: chainId,
    calls: vaultCalls as any,
    // buildFluidCall mixes vault-resolver and liquidity-resolver calls; the
    // merged ABI lets viem resolve each call by function name.
    abi: vaultCalls.map(() => [
      ...FluidVaultResolverAbi,
      ...FluidLiquidityResolverAbi,
    ]) as any,
  })

  const [convert] = getFluidPublicDataConverter(
    'FLUID',
    chainId,
    prices,
    { intrinsicYields: {}, lenderRewards: {}, loaded: true },
    tokenList,
  )
  const lenderMap = convert(rawVaults) ?? {}

  // Collect raw rows per loan asset; weights are filled once per-asset
  // totals are known.
  type Row = Omit<VaultMarketExposure, 'weightPct'>
  const rowsByLoan = new Map<string, Row[]>()
  for (const lender of Object.keys(lenderMap)) {
    const entry = lenderMap[lender]
    const vault = entry?.params?.vault
    if (!vault) continue
    const loanAddress = (vault.loanAddress ?? '').toLowerCase()
    const collateralAddress = (vault.collateralAddress ?? '').toLowerCase()
    if (!loanAddress) continue

    // The loan-side market entry carries the borrowed totals + borrow rate.
    const loanEntry = Object.values(entry.data ?? {}).find(
      (d: any) => d?.borrowingEnabled && d?.underlying === loanAddress,
    ) as any
    if (!loanEntry) continue

    const assets = Number(loanEntry.totalDebt ?? 0)
    if (!(assets > 0)) continue
    const assetsUsd = Number(loanEntry.totalDebtUSD ?? 0) || 0

    const row: Row = {
      marketId: (vault.vaultAddress ?? '').toLowerCase(),
      collateralAddress,
      collateral: collateralAddress ? tokenList[collateralAddress] : undefined,
      assets,
      assetsUsd,
      supplyApr: Number(loanEntry.variableBorrowRate ?? 0) || 0,
    }
    const list = rowsByLoan.get(loanAddress)
    if (list) list.push(row)
    else rowsByLoan.set(loanAddress, [row])
  }

  for (const [loanAddress, rows] of rowsByLoan) {
    // Weight by USD when available, else by raw assets (all the same token).
    const totalUsd = rows.reduce((s, r) => s + r.assetsUsd, 0)
    const totalAssets = rows.reduce((s, r) => s + r.assets, 0)
    const useUsd = totalUsd > 0
    const denom = useUsd ? totalUsd : totalAssets
    if (denom <= 0) continue
    const exposures: VaultMarketExposure[] = rows
      .map((r) => ({
        ...r,
        weightPct: ((useUsd ? r.assetsUsd : r.assets) / denom) * 100,
      }))
      .sort((a, b) => b.weightPct - a.weightPct)
    out.set(loanAddress, exposures)
  }

  return out
}
